import MENSAGEM_VALIDACAO from '@/constants/MensagensValidacao'
import { compareMaxDate, compareMinDate, isOver18years, isUnderLimitAge, parseDateFromString, getTomorrowDate, getNextMonthLastDay } from './date'
import { DateTime } from 'luxon'
import { CPFUtil } from './CpfUtil'

/**
 * Validação canônica de CNPJ com suporte ao formato alfanumérico
 * (IN RFB 2229/2024): 12 posições base [A-Z0-9] + 2 dígitos verificadores [0-9].
 * O dígito verificador usa o valor do caractere = (código ASCII - 48),
 * mantendo compatibilidade total com CNPJs numéricos legados.
 * Retorna true também para valor vazio (campo opcional).
 */
export function isValidCnpjAlnum(value: any): boolean {
	if (value == null) return false
	const cnpj = CPFUtil.cleanCnpj(value.toString())
	if (cnpj === '') return true
	if (!/^[A-Z0-9]{12}\d{2}$/.test(cnpj)) return false
	if (/^(.)\1{13}$/.test(cnpj)) return false // elimina sequências repetidas

	const charVal = (c: string): number => c.charCodeAt(0) - 48
	const calcDigit = (base: string): number => {
		const len = base.length
		let soma = 0
		let pos = len - 7
		for (let i = len; i >= 1; i--) {
			soma += charVal(base.charAt(len - i)) * pos--
			if (pos < 2) pos = 9
		}
		const mod = soma % 11
		return mod < 2 ? 0 : 11 - mod
	}

	const base = cnpj.substring(0, 12)
	const d1 = calcDigit(base)
	const d2 = calcDigit(base + d1)
	return d1 === parseInt(cnpj.charAt(12), 10) && d2 === parseInt(cnpj.charAt(13), 10)
}

// TODO: REMOVER ESSE VALIDATIONS ESPECIFIVOS E USAR A CLASSE VALIDATIONS
/**
 * @deprecated utilizar a classe Validations
 */
export const ValidationsEspecificos = {
	/**
	 * Validação básica para CEP
	 */
	Email: {
		isRequired: (v: any) => !!v || 'O E-mail é obrigatório',
		isValid: (v: any) => {
			const pattern =
				/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
			return pattern.test(v) || 'E-mail digitado é inválido'
		}
	},
	CPF: {
		isRequired: (v: any) => !!v || 'O CPF é obrigatório',
		isValid: (v: any) => ValidationsEspecificos.testCPF(v) || 'CPF digitado é inválido'
	},
	CNPJ: {
		isRequired: (v: any) => !!v || 'O CNPJ é obrigatório',
		isValid: (v: any) => ValidationsEspecificos.testCNPJ(v) || 'CNPJ digitado é inválido'
	},
	/**
	 * @private
	 * @param cpf Suporta somente numeros ou alfanumericos CPF para testart
	 * @returns boolean
	 */
	testCPF(cpf: any) {
		if (cpf == null || cpf === '' || cpf == '000.000.000-00') return true
		if (cpf) {
			if (typeof cpf !== 'string') cpf = cpf.toString()
			cpf = cpf.replace(/\D/g, '')
			const mod11 = (num: number) => num % 11
			const NOT = (x: boolean) => !x
			const isEqual = (a: string) => (b: string) => b === a
			const mergeDigits = (num1: number, num2: number) => `${num1}${num2}`
			const getTwoLastDigits = (cpf: any[]) => `${cpf[9]}${cpf[10]}`
			const getCpfNumeral = (cpf: any) => cpf.substr(0, 9).split('')
			const isRepeatingChars = (str: string) => str.split('').every((elem: any) => elem === str[0])
			const toSumOfProducts = (multiplier: number) => (result: number, num: number, i: any) => result + num * multiplier--
			const getSumOfProducts = (list: any, multiplier: number) => list.reduce(toSumOfProducts(multiplier), 0)
			const getValidationDigit = (multiplier: number) => (cpf: any) => getDigit(mod11(getSumOfProducts(cpf, multiplier)))
			const getDigit = (num: number) => (num > 1 ? 11 - num : 0)
			const isRepeatingNumbersCpf = isRepeatingChars
			const isValidCPF = (cpf: any) => {
				const CPF = getCpfNumeral(cpf)
				const firstDigit = getValidationDigit(10)(CPF)
				const secondDigit = getValidationDigit(11)(CPF.concat(firstDigit))
				return isEqual(getTwoLastDigits(cpf))(mergeDigits(firstDigit, secondDigit))
			}
			return NOT(isRepeatingNumbersCpf(cpf)) && isValidCPF(cpf)
		}
	},
	/**
	 * @private
	 * @param cnpj Suporta somente numeros ou alfanumericos CNPJ para testart
	 * @returns boolean
	 */
	testCNPJ(cnpj: any) {
		return isValidCnpjAlnum(cnpj)
	}
}

export class Validations {
	/**
	 * Verifica se o campo é obrigatório
	 * @param value
	 * @returns
	 */
	static isRequired(value: string | number) {
		return !!value || MENSAGEM_VALIDACAO.CAMPO_OBRIGATORIO
	}

	/**
	 * Função genérica para validar se a data é válida
	 * Também valida a data de emissão mínima para cada tipo de documento
	 *
	 * @param docDate // passa a data no formato dd/mm/yyyy
	 * @param docType // passa o tipo de documento
	 * @param birthDate // passa a data de nascimento no formato dd/mm/yyyy
	 * @returns boolean or string with error message
	 */
	static IsDateValidForDocumentType(docDate: string, docType: string, birthDate: string) {
		try {
			if (docDate) {
				const [day, month, year] = docDate.split('/')
				const docDateTime = DateTime.fromJSDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)))

				const [birthDay, birthMonth, birthYear] = birthDate.split('/')
				const birthDateTime = DateTime.fromJSDate(new Date(parseInt(birthYear), parseInt(birthMonth) - 1, parseInt(birthDay)))

				if (docType === 'RNE') {
					const limitDateDocValid = DateTime.now().minus({ years: 10 })
					if (docDateTime < limitDateDocValid) return MENSAGEM_VALIDACAO.DATA_INVALIDA ?? true
				}

				if (docType === 'RG') {
					// Case person is older than 60 years, the document will be valid
					const limitDate = DateTime.now().minus({ years: 60 })
					if (birthDateTime < limitDate) return true

					// Case person is younger than 60 years, the document will be valid for 20 years
					const limitDateDocValid = DateTime.now().minus({ years: 20 })
					if (docDateTime < limitDateDocValid) return MENSAGEM_VALIDACAO.DATA_INVALIDA ?? true
				}
			}

			return true
		} catch {
			return false
		}
	}

	/**
	 * Function to validate if date is before 1900
	 * @param date // date in format dd/mm/yyyy
	 * @returns boolean
	 */
	static isDateBefore1900(date: string) {
		try {
			if (date) {
				const [day, month, year] = date.split('/')
				const finalYear = new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).getFullYear()

				return finalYear < 1900
			}
		} catch {
			return false
		}
	}

	/**
	 * Function to validate if date is greater than 80.5 years
	 * @param date // string with date in format dd/mm/yyyy
	 * @param term // number with term in months
	 * @returns boolean or string with error message
	 */
	static isDateGreaterThan805(date: string, term?: number) {
		if (!date) return false

		try {
			const now = new Date(new Date().setHours(0, 0, 0, 0))
			const [day, month, year] = date.split('/')

			// To prevent errors in the bank validation, we subtracted 32 days from the date of birth to prevent errors on submit proposal
			let birthdayDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day) - 32, 0, 0, 0)

			// If have a loan term, subtract it from the birthday date
			if (term !== undefined && Number.isInteger(Number(term))) {
				birthdayDate.setMonth(birthdayDate.getMonth() - term)
			}

			const differenceInMilliseconds = now.getTime() - birthdayDate.getTime()
			const millisecondsInYear = 1000 * 60 * 60 * 24 * 365.25
			const years = differenceInMilliseconds / millisecondsInYear

			return years >= 80.5 ? MENSAGEM_VALIDACAO.AGE_AND_TERM_OVER_805 : false
		} catch {
			return MENSAGEM_VALIDACAO.DATA_INVALIDA
		}
	}

	/**
	 * Faz uma comparação entre dois campos para verificar se é maior
	 * @param value
	 * @param compareValue
	 * @param msg
	 * @returns
	 */
	static isGreaterThan(value: any, compareValue: any, msg: string) {
		if (!value) return true

		return parseFloat(value) > parseFloat(compareValue) || msg
	}

	/**
	 * Faz uma comparação entre dois campos para verificar se é menor
	 * @param value
	 * @param compareValue
	 * @param msg
	 * @returns
	 */
	static isLessThan(value: any, compareValue: any, msg: string) {
		return parseFloat(value) < parseFloat(compareValue) || msg
	}

	/**
	 * Faz uma comparação entre dois campos para verificar se é menor ou igual
	 * @param value
	 * @param compareValue
	 * @param msg
	 * @returns
	 */
	static isLessOrEqual(value: any, compareValue: any, msg: string) {
		return parseFloat(value) <= parseFloat(compareValue) || msg
	}

	/**
	 * Verifica se o valor possui o tamanho correto
	 * @param value
	 * @param length
	 * @returns
	 */
	static isLengthValid(value: any, length: number) {
		if (!value) return `O valor precisa conter ${length} dígitos`
		return value.length == length || `O valor precisa conter ${length} dígitos`
	}

	/**
	 * Comparar a data fornecida e a data maxima, para ver se a data fornecida é menor
	 * @param strData
	 * @param dataMaxima
	 * @returns
	 */
	static isDateGreaterThan(strData: string, dataMaxima: string | null = null, message: string | null) {
		if (!strData) return false

		if(!message)
			message = MENSAGEM_VALIDACAO.DATA_MAIOR_DATA_MAXIMA

		return compareMaxDate(strData, dataMaxima) || message
	}

	/**
	 * Comparar a data fornecida e a data minima, para ver se a data fornecida é maior
	 * @param strData
	 * @param dataMinima
	 * @returns
	 */
	static isDateLessThan(strData: string, dataMinima: string | null = null, message: string | null) {
		if(!message)
			message = MENSAGEM_VALIDACAO.DATA_MENOR_DATA_MINIMA

		return compareMinDate(strData, dataMinima) || message
	}

	/**
	 * Verifica se tem pelo menos 18 anos
	 * @param strData
	 * @param dataMaxima
	 * @returns
	 */
	static isAgeOver18(data: string) {
		if (!data) return true

		return isOver18years(data) || MENSAGEM_VALIDACAO.NAO_TEM_18_ANOS
	}

	/**
	 * Verifica se tem menos que a idade limite
	 * @param data
	 * @param limit
	 * @returns
	 */
	static isAgeUnderLimit(data: string, limit: number) {
		if (!data) return true

		return isUnderLimitAge(data, limit) || MENSAGEM_VALIDACAO.IDADE_LIMITE_80_5
	}

	/**
	 * Verifica se data informada é uma data valida
	 * @param data
	 * @returns
	 */
	static ValidDate(data: string) {
		if (data == null) return true

		if (!data) return false

		let date = parseDateFromString(data)
		return date.isValid || MENSAGEM_VALIDACAO.DATA_INVALIDA
	}

	/**
	 * Faz uma comparação entre dois campos para verificar se é maior ou igual
	 * @param value
	 * @param compareValue
	 * @param msg
	 * @returns
	 */
	static isGreaterOrEqualThan(value: any, compareValue: any, msg: string) {
		return parseFloat(value) >= parseFloat(compareValue) || msg
	}

	/**
	 * Faz uma comparação entre dois campos para verificar se é maior ou igual
	 * @param value
	 * @param compareValue
	 * @param msg
	 * @returns
	 */
	static containsLetter(value: any, msg: string) {
		return !/[a-zA-Z]/.test(value) || msg
	}

	/**
	 * Verifica se o valor possui o tamanho mínimo
	 * @param value
	 * @param minLength
	 * @returns
	 */
	static isMinLength(value: string, minLength: number) {
		return value.length >= length || `O valor precisa conter pelo menos ${minLength} dígitos`
	}

	/**
	 * Verifica se o valor possui o tamanho máximo
	 * @param value
	 * @param maxLength
	 * @returns
	 */
	static isMaxLength(value: string, maxLength: number) {
		return value.length <= length || `O valor precisa conter no máximo ${maxLength} dígitos`
	}

	/**
	 * Verify if date is valid
	 * @param value date to validate
	 */
	static isValidDate(value: string) {
		if (!value) return true

		const date = parseDateFromString(value)

		return date.isValid
	}

	static isCPFValid(value: string) {
		return ValidationsEspecificos.testCPF(value) || MENSAGEM_VALIDACAO.CPF_INVALID
	}

	/**
	 * Check if the cell phone digit is valid
	 * @param value
	 * @returns
	 */
	static isCellPhoneDigitValid(value: string) {
		if (!value) return true

		return (value && value.length > 5 && value.charAt(5) == '9') || MENSAGEM_VALIDACAO.CELLPHONE_INVALID_DIGIT
	}

	/**
	 * Check if the phone DDD is valid
	 * @param value
	 * @returns
	 */
	static isPhoneDddValid(value: string) {
		if (!value) return true

		if (!!value) {
			const ddds = [
				11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43, 44, 45, 46, 47, 48, 49, 51, 53, 54,
				55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98,
				99
			]
			const ddd = parseInt(value.substring(1, 3))

			return ddds.includes(ddd) || MENSAGEM_VALIDACAO.PHONE_INVALID_DDD
		}
	}

	/**
	 * Check if the CNPJ Document is valid
	 * @param cnpj
	 * @returns
	 */
	static isCNPJValid(cnpj: string | null) {
		if (cnpj == null) return MENSAGEM_VALIDACAO.CNPJ_INVALID
		return isValidCnpjAlnum(cnpj) || MENSAGEM_VALIDACAO.CNPJ_INVALID
	}

	/**
	 * Verify if email is valid
	 * @param value email to validate
	 * @returns boolean
	 */
	static isValidEmail(value: string) {
		if (!value) return true

		const emailRegex =
			/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

		return emailRegex.test(value) || MENSAGEM_VALIDACAO.EMAIL_INVALIDO
	}

	/**
	 * Verify if the cell phone is valid
	 * @param value
	 * @returns
	 */
	static isCellPhoneValid(value: string) {
		const regex = /^\(?\d{2}\)?[- ]?\d{5}[- ]?\d{4}$/

		// Remove caracteres não numéricos do número de celular
		const number = value != null ? value.replace(/[^\d]/g, '') : '0'

		// Verifica se o número de celular corresponde à expressão regular
		return regex.test(number) || MENSAGEM_VALIDACAO.CELLPHONE_INVALID_DIGIT
	}

	/**
	 * Validate if the phone is valid
	 * @param value
	 * @returns
	 */
	static isPhoneValid(value: string) {
		const regex = /^\(?\d{2}\)?[- ]?\d{4}[- ]?\d{4}$/

		// Remove caracteres não numéricos do número de celular
		//const number = value.replace(/[^\d]/g, '');
		const number = value != null ? value.replace(/[^\d]/g, '') : '0'

		// Verifica se o número de celular corresponde à expressão regular
		return regex.test(number) || MENSAGEM_VALIDACAO.PHONE_INVALID
	}

	/**
	 * Verify if two value are different
	 * @param string
	 * @param compareString
	 * @param msg
	 * @returns
	 */
	static isDifferent(string: any, compareString: any, msg: string) {
		return string != compareString || msg
	}
}
export default Validations
